// pages/NotaireMessagingPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";

import { useMemberstack } from "../context/MemberstackContext";
import { fetchInbox, sendMessage, markThreadRead } from "../functions/apiMessage";

// --------------------
// helpers
// --------------------
function normalizeEmail(v) {
  return String(v || "").trim().toLowerCase();
}

function normalizeAffaireId(v) {
  if (!v) return "";
  if (Array.isArray(v)) return String(v[0] || "").trim();
  return String(v).trim();
}

function formatFR(ts) {
  if (!ts) return "Date inconnue";
  return new Date(String(ts))
    .toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "h");
}

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return (tmp.textContent || tmp.innerText || "").trim();
}

function pickPhone(obj, side /* 'sender' | 'receiver' */) {
  const keys = [`telephone_${side}`, `tel_${side}`, `phone_${side}`, `mobile_${side}`];
  for (const k of keys) {
    const v = obj?.[k];
    if (v) return String(v).trim();
  }
  return "";
}

function pickContactIdentityFromMessage(msg, me) {
  const mine = normalizeEmail(me);
  const sender = normalizeEmail(msg?.sender);
  const receiver = normalizeEmail(msg?.receiver);

  const isMineSender = sender === mine;
  const contactEmail = isMineSender ? receiver : sender;

  const contactPrenom = isMineSender ? msg?.prenom_receiver : msg?.prenom_sender;
  const contactNom = isMineSender ? msg?.nom_receiver : msg?.nom_sender;
  const contactRole = isMineSender ? msg?.role_receiver : msg?.role_sender;

  const contactTelephone = isMineSender ? pickPhone(msg, "receiver") : pickPhone(msg, "sender");

  const prenom = String(contactPrenom || "").trim();
  const nom = String(contactNom || "").trim();
  const full = `${prenom} ${nom}`.trim();

  return {
    contactEmail,
    contactPrenom: prenom,
    contactNom: nom,
    contactFullName: full,
    contactRole: String(contactRole || "").trim(),
    contactTelephone: String(contactTelephone || "").trim(),
  };
}

function displayContactName(c) {
  const prenom = String(c?.contactPrenom || "").trim();
  const nom = String(c?.contactNom || "").trim();
  const full = `${prenom} ${nom}`.trim();
  return full || c?.contactEmail || "—";
}

function roleBadgeClasses(roleRaw) {
  const role = String(roleRaw || "").toLowerCase().trim();
  if (role.includes("notaire")) return "bg-indigo-50 text-indigo-700 ring-indigo-200";
  if (role.includes("acheteur")) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (role.includes("vendeur")) return "bg-amber-50 text-amber-800 ring-amber-200";
  if (role.includes("agent")) return "bg-sky-50 text-sky-700 ring-sky-200";
  if (role.includes("partenaire") || role.includes("courtier"))
    return "bg-purple-50 text-purple-700 ring-purple-200";
  return "bg-gray-50 text-gray-700 ring-gray-200";
}

// --------------------
// mapping type_affaire -> route
// --------------------
const AFFAIRE_TYPE_TO_ROUTE = {
  // ✅ immo
  immobilier: "immo",
  immo: "immo",
  transaction: "immo",

  // ✅ autres (routes identiques au type)
  pma: "pma",
  mariage: "mariage",
  pacs: "pacs",
  certification: "certification",
  divorce: "divorce",
  bail: "bail",
  donation: "donation",
  succession: "succession",
  testament: "testament",
  "statut-societe": "statut-societe",
  adoption: "adoption",
  procuration: "procuration",
};

function formatTypeLabel(typeAffaireRaw) {
  const t = String(typeAffaireRaw || "").toLowerCase().trim();
  if (!t) return "—";

  // ✅ joli label pour immo
  if (t === "immobilier" || t === "immo" || t === "transaction") return "Immobilier";

  // ✅ autres: Title Case (avec tirets)
  return t
    .split("-")
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(" ");
}

function buildAffaireLink(typeAffaire, affaireId) {
  const type = String(typeAffaire || "").toLowerCase().trim();
  const route = AFFAIRE_TYPE_TO_ROUTE[type];
  const id = String(affaireId || "").trim();

  if (!route || !id) return null;

  // ✅ donnera /app/affaire/immo/<id> ou /app/affaire/pacs/<id>
  return `/app/affaire/${route}/${id}`;
}

export default function NotaireMessagingPage() {
  const { member, loading } = useMemberstack();
  const me = member?.auth?.email ? normalizeEmail(member.auth.email) : "";

  const [allMessages, setAllMessages] = useState([]);
  const [activeContact, setActiveContact] = useState(null); // email
  const [activeAffaireId, setActiveAffaireId] = useState(""); // string
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  const messagesEndRef = useRef(null);

  const loadAll = async () => {
    if (!me) return;
    setIsFetching(true);
    try {
      const data = await fetchInbox({ me });

      const cleaned = (Array.isArray(data) ? data : []).map((m) => ({
        ...m,
        sender: normalizeEmail(m?.sender),
        receiver: normalizeEmail(m?.receiver),
        id_affaire: normalizeAffaireId(m?.id_affaire),
      }));

      setAllMessages(cleaned);
    } catch (e) {
      console.error("❌ load inbox error:", e);
      toast.error("Impossible de charger la messagerie.");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!loading && me) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, me]);

  // ✅ map affaireId -> meta (name/type/url)
  const affaireMetaById = useMemo(() => {
    const m = new Map(); // affaireId -> {name, type, url}

    for (const msg of allMessages) {
      const id = msg?.id_affaire;
      if (!id) continue;

      const prev = m.get(id) || {};
      m.set(id, {
        affaireId: id,
        name: msg?.affaire_name || prev.name || null,
        type: msg?.type_affaire || prev.type || null,
        url:
          msg?.affaire_url ||
          prev.url ||
          buildAffaireLink(msg?.type_affaire, id) ||
          null,
      });
    }

    return m;
  }, [allMessages]);

  function affairLabel(affaireId) {
    const id = String(affaireId || "");
    if (!id) return "Sans affaire";
    const meta = affaireMetaById.get(id);
    const name = meta?.name || `Affaire ${id.slice(-6)}`;
    const typeLabel = meta?.type ? formatTypeLabel(meta.type) : null;
    return typeLabel ? `${name} • ${typeLabel}` : name;
  }

  // ✅ Contacts list: 1 contact => plusieurs affaires
  const contacts = useMemo(() => {
    const mine = normalizeEmail(me);
    const map = new Map(); // contactEmail -> contactObject

    for (const msg of allMessages) {
      if (msg.sender !== mine && msg.receiver !== mine) continue;

      const ident = pickContactIdentityFromMessage(msg, mine);
      const contactEmail = normalizeEmail(ident?.contactEmail);
      if (!contactEmail) continue;

      const affaireId = normalizeAffaireId(msg?.id_affaire); // peut être ""
      const ts = msg.timestamp ? new Date(String(msg.timestamp)).getTime() : 0;

      const isIncoming = msg.receiver === mine;
      const isUnread = isIncoming && (msg.read === false || msg.read === "false" || msg.read == null);

      const c =
        map.get(contactEmail) || {
          contactEmail,
          contactPrenom: "",
          contactNom: "",
          contactFullName: "",
          contactRole: "",
          contactTelephone: "",
          lastTimestamp: null,
          lastMessagePreview: "",
          unreadTotal: 0,
          affaires: new Map(), // affaireId -> { affaireId, lastTimestamp, lastMessagePreview, unreadCount }
        };

      // identité
      if (!c.contactPrenom && ident.contactPrenom) c.contactPrenom = ident.contactPrenom;
      if (!c.contactNom && ident.contactNom) c.contactNom = ident.contactNom;
      if (!c.contactRole && ident.contactRole) c.contactRole = ident.contactRole;
      if (!c.contactTelephone && ident.contactTelephone) c.contactTelephone = ident.contactTelephone;
      c.contactFullName = `${c.contactPrenom} ${c.contactNom}`.trim();

      // unread global
      if (isUnread) c.unreadTotal += 1;

      // thread par affaire
      const t =
        c.affaires.get(affaireId) || {
          affaireId,
          lastTimestamp: null,
          lastMessagePreview: "",
          unreadCount: 0,
        };

      if (isUnread) t.unreadCount += 1;

      const tts = t.lastTimestamp ? new Date(String(t.lastTimestamp)).getTime() : 0;
      if (!t.lastTimestamp || ts >= tts) {
        t.lastTimestamp = msg.timestamp || t.lastTimestamp;
        t.lastMessagePreview = stripHtml(msg.content || "").slice(0, 90);

        // refresh identité sur dernier msg
        if (ident.contactRole) c.contactRole = ident.contactRole;
        if (ident.contactTelephone) c.contactTelephone = ident.contactTelephone;
        if (ident.contactPrenom) c.contactPrenom = ident.contactPrenom;
        if (ident.contactNom) c.contactNom = ident.contactNom;
        c.contactFullName = `${c.contactPrenom} ${c.contactNom}`.trim();
      }

      c.affaires.set(affaireId, t);

      // last global
      const cts = c.lastTimestamp ? new Date(String(c.lastTimestamp)).getTime() : 0;
      if (!c.lastTimestamp || ts >= cts) {
        c.lastTimestamp = msg.timestamp || c.lastTimestamp;
        c.lastMessagePreview = stripHtml(msg.content || "").slice(0, 90);
      }

      map.set(contactEmail, c);
    }

    let arr = Array.from(map.values()).map((c) => {
      const affairesArr = Array.from(c.affaires.values());
      affairesArr.sort((a, b) => {
        const ta = a.lastTimestamp ? new Date(String(a.lastTimestamp)).getTime() : 0;
        const tb = b.lastTimestamp ? new Date(String(b.lastTimestamp)).getTime() : 0;
        return tb - ta;
      });
      return { ...c, affairesArr };
    });

    const q = normalizeEmail(search);
    if (q) {
      arr = arr.filter((c) => {
        const email = normalizeEmail(c.contactEmail);
        const name = normalizeEmail(c.contactFullName);
        return email.includes(q) || name.includes(q);
      });
    }

    arr.sort((a, b) => {
      const ta = a.lastTimestamp ? new Date(String(a.lastTimestamp)).getTime() : 0;
      const tb = b.lastTimestamp ? new Date(String(b.lastTimestamp)).getTime() : 0;
      return tb - ta;
    });

    return arr;
  }, [allMessages, me, search]);

  // ✅ auto-select contact + affaire
  useEffect(() => {
    if (!activeContact && contacts.length > 0) {
      const first = contacts[0];
      setActiveContact(first.contactEmail);
      setActiveAffaireId(first.affairesArr?.[0]?.affaireId || "");
      return;
    }

    if (activeContact) {
      const found = contacts.find((c) => normalizeEmail(c.contactEmail) === normalizeEmail(activeContact));
      if (!found && contacts.length > 0) {
        setActiveContact(contacts[0].contactEmail);
        setActiveAffaireId(contacts[0].affairesArr?.[0]?.affaireId || "");
        return;
      }
      if (found) {
        const exists = found.affairesArr?.some((t) => String(t.affaireId) === String(activeAffaireId));
        if (!exists) setActiveAffaireId(found.affairesArr?.[0]?.affaireId || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts.length, activeContact]);

  const activeContactObj = useMemo(() => {
    if (!activeContact) return null;
    return contacts.find((c) => normalizeEmail(c.contactEmail) === normalizeEmail(activeContact)) || null;
  }, [contacts, activeContact]);

  const activeAffaires = activeContactObj?.affairesArr || [];

  const activeAffaireMeta = useMemo(() => {
    if (!activeAffaireId) return null;
    return affaireMetaById.get(String(activeAffaireId)) || null;
  }, [activeAffaireId, affaireMetaById]);

  const activeAffaireUrl = useMemo(() => {
    if (!activeAffaireId) return null;
    return (
      activeAffaireMeta?.url ||
      buildAffaireLink(activeAffaireMeta?.type, activeAffaireId) ||
      null
    );
  }, [activeAffaireMeta, activeAffaireId]);

  // ✅ messages affichés = (me <-> contact) + id_affaire
  const activeMessages = useMemo(() => {
    if (!activeContact) return [];
    const mine = normalizeEmail(me);
    const contact = normalizeEmail(activeContact);
    const aid = String(activeAffaireId || "");

    const filtered = allMessages.filter((m) => {
      const s = normalizeEmail(m.sender);
      const r = normalizeEmail(m.receiver);
      const okPair = (s === mine && r === contact) || (s === contact && r === mine);
      if (!okPair) return false;

      const mid = normalizeAffaireId(m.id_affaire);
      return String(mid || "") === String(aid || "");
    });

    filtered.sort((a, b) => {
      const ta = a.timestamp ? new Date(String(a.timestamp)).getTime() : 0;
      const tb = b.timestamp ? new Date(String(b.timestamp)).getTime() : 0;
      return ta - tb;
    });

    return filtered;
  }, [allMessages, me, activeContact, activeAffaireId]);

  // scroll bottom
  useEffect(() => {
    const id = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
    return () => clearTimeout(id);
  }, [activeContact, activeAffaireId, activeMessages.length]);

  // ✅ mark read quand on ouvre un thread (contact + affaire)
  useEffect(() => {
    if (!me || !activeContact) return;
    if (typeof markThreadRead !== "function") return;

    (async () => {
      try {
        await markThreadRead({
          me,
          contactEmail: activeContact,
          affaireId: activeAffaireId || "",
        });
        await loadAll();
      } catch (e) {
        console.error("❌ markThreadRead error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, activeContact, activeAffaireId]);

  const handleSend = async () => {
    if (!message.trim()) return;
    if (!activeContact) return;

    if (!activeAffaireId) {
      toast.error("Impossible d’envoyer: id_affaire manquant.");
      return;
    }

    try {
      const ok = await sendMessage(me, activeContact, message, { id: activeAffaireId });
      if (ok) {
        toast.success("Message envoyé !");
        setMessage("");
        await loadAll();
      } else {
        toast.error("Erreur d'envoi du message");
      }
    } catch (e) {
      console.error("❌ send error:", e);
      toast.error("Erreur d'envoi du message");
    }
  };

  if (loading || !member) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Chargement de la messagerie...</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] bg-white/20">
      <div className="mx-auto max-w-7xl h-full">
        <div className="h-full grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* LEFT: contacts */}
          <div className="md:col-span-4 lg:col-span-3 bg-white rounded-2xl shadow overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <p className="text-base font-semibold text-gray-900 mb-2">Messagerie</p>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par email / nom…"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              />
              {isFetching ? <p className="text-xs text-gray-500 mt-2 mb-0">Chargement…</p> : null}
            </div>

            <ul className="divide-y divide-gray-100 max-h-[calc(100vh-180px)] overflow-y-auto">
              {contacts.length === 0 ? (
                <li className="p-4 text-sm text-gray-500">Aucune conversation.</li>
              ) : (
                contacts.map((c) => {
                  const isActive = normalizeEmail(activeContact) === normalizeEmail(c.contactEmail);

                  return (
                    <li key={c.contactEmail}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveContact(c.contactEmail);
                          setActiveAffaireId(c.affairesArr?.[0]?.affaireId || "");
                        }}
                        className={[
                          "w-full text-left flex flex-col items-start justify-between gap-1 px-3 py-3 hover:bg-gray-50",
                          isActive ? "bg-indigo-50" : "",
                        ].join(" ")}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate mb-0">
                            {displayContactName(c)}
                          </p>
                          {/** 
                            <p className="mt-1 truncate text-xs text-gray-500 mb-0">
                              {c.contactEmail}
                            </p>
                          */}
                          <p className="mt-1 truncate text-xs text-gray-500 mb-0">
                            {c.lastMessagePreview || "—"}
                          </p>
                        </div>

                        <div className="shrink-0 flex flex-col items-end gap-1">
                          <p className="text-xs text-gray-500 mb-0">{formatFR(c.lastTimestamp)}</p>
                          {c.unreadTotal > 0 ? (
                            <span className="inline-flex items-center rounded-full bg-indigo-600 text-white text-xs font-semibold px-2 py-0.5">
                              {c.unreadTotal}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {/* RIGHT: conversation */}
          <div className="md:col-span-8 lg:col-span-9 bg-white rounded-2xl shadow overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-base font-semibold text-gray-900 mb-0 truncate">
                      {activeContactObj ? displayContactName(activeContactObj) : "Sélectionnez une conversation"}
                    </p>

                    {activeContactObj?.contactRole ? (
                      <span
                        className={[
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                          roleBadgeClasses(activeContactObj.contactRole),
                        ].join(" ")}
                      >
                        {activeContactObj.contactRole}
                      </span>
                    ) : null}
                  </div>

                  {activeContactObj ? (
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <p className="text-xs text-gray-500 mb-0 truncate">{activeContactObj.contactEmail}</p>
                      {activeContactObj.contactTelephone ? (
                        <a
                          href={`tel:${activeContactObj.contactTelephone}`}
                          className="text-xs text-gray-500 hover:text-gray-700 mb-0"
                        >
                          {activeContactObj.contactTelephone}
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mb-0">Sélectionnez un contact pour afficher la discussion.</p>
                  )}
                </div>

                {/* ✅ Bouton Voir l’affaire */}
                {activeAffaireUrl ? (
                  <Link
                    to={activeAffaireUrl}
                    className="shrink-0 inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 no-underline"
                  >
                    Voir l’affaire
                  </Link>
                ) : null}
              </div>


              {/* ✅ Tabs par affaire (avec triangle) */}
              {activeContactObj ? (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                  {activeAffaires.map((t) => {
                    const isOn = String(t.affaireId) === String(activeAffaireId);
                    return (
                      <button
                        key={String(t.affaireId)}
                        type="button"
                        onClick={() => setActiveAffaireId(t.affaireId)}
                        className={[
                          "relative shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1",
                          isOn
                            ? "bg-indigo-600 text-white ring-indigo-600"
                            : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50",
                        ].join(" ")}
                        title={String(t.affaireId || "")}
                      >
                        {affairLabel(t.affaireId)}
                        {t.unreadCount > 0 ? (
                          <span
                            className={[
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                              isOn ? "bg-white/20 text-white" : "bg-indigo-600 text-white",
                            ].join(" ")}
                          >
                            {t.unreadCount}
                          </span>
                        ) : null}

                        {/* 🔻 triangle sous l’onglet actif */}
                        {isOn ? (
                          <span
                            className="absolute left-1/2 -bottom-2 h-0 w-0 -translate-x-1/2
                                       border-l-[8px] border-r-[8px] border-t-[8px]
                                       border-l-transparent border-r-transparent border-t-indigo-600"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-white/20">
              {activeContactObj && activeMessages.length === 0 ? (
                <div className="text-center">
                  <h2 className="mt-2 text-base font-semibold text-gray-900">Aucun message pour l’instant</h2>
                  <p className="mt-1 text-sm text-gray-500">Envoyez un premier message sur cette affaire.</p>
                </div>
              ) : null}

              <ul className="space-y-8 grid grid-cols-1">
                {activeMessages.map((msg, idx) => {
                  const isMine = normalizeEmail(msg.sender) === normalizeEmail(me);

                  if (isMine) {
                    return (
                      <li key={`${msg.id || idx}-mine`}>
                        <div className="p-3 rounded-2xl space-y-2 bg-slate-300 text-right text-black place-self-end rounded-tr-none max-w-[85%] ml-auto">
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-right">{formatFR(msg.timestamp)}</p>
                      </li>
                    );
                  }

                  return (
                    <li key={`${msg.id || idx}-other`}>
                      <div className="flex flex-row justify-start gap-2">
                        <div className="max-w-[85%]">
                          <div className="p-3 rounded-2xl space-y-2 bg-indigo-500 text-left text-white rounded-tl-none">
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatFR(msg.timestamp)}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}

                <div ref={messagesEndRef} />
              </ul>
            </div>

            {/* Input */}
            <div className="sticky bottom-0 left-0 w-full bg-white p-3 shadow-md">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="Écrivez votre message..."
                rows={4}
                disabled={!activeContactObj || !activeAffaireId}
              />
              <button
                onClick={handleSend}
                disabled={!activeContactObj || !activeAffaireId || !message.trim()}
                className={[
                  "bg-blue-600 text-white px-4 py-2 mt-2 rounded-lg w-full",
                  !activeContactObj || !activeAffaireId || !message.trim()
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-blue-700",
                ].join(" ")}
                type="button"
              >
                Envoyer
              </button>
              {!activeAffaireId && activeContactObj ? (
                <p className="text-xs text-amber-700 mt-2 mb-0">
                  Aucun id_affaire détecté pour ce contact: impossible d’envoyer depuis la messagerie globale.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}